/* ============================================================
   STORE 2 — Automatic free gifts

   Runs up to two independent gift promos:
     - "order"     one free gift while the cart holds a paid item
     - "threshold" one free gift while the paid subtotal is at or
                   above that gift's configured minimum

   Each keeps at most ONE unit of its own gift line in the cart,
   added when its condition is met and removed the moment it no
   longer is. Order-level, never 1:1 per product added.

   Strategy: don't try to predict cart state from individual
   actions — after any cart mutation, read /cart.js and settle
   the difference against every configured gift. That makes it
   correct regardless of who changed the cart (our ATC, Dawn,
   quick-add, an app, or a plain form post).
============================================================ */
(function () {
  var cfg = window.s2GiftConfig || {};
  var GIFTS = (cfg.gifts || []).filter(function (g) {
    return g && parseInt(g.variantId, 10);
  });
  if (!GIFTS.length) return;

  GIFTS.forEach(function (g) { g.variantId = parseInt(g.variantId, 10); });

  var R = window.routes || {};
  var CART = R.cart_url || '/cart';
  var ADD = R.cart_add_url || '/cart/add';
  var CHANGE = R.cart_change_url || '/cart/change';
  var PROP = '_free_gift';

  /* Native fetch, captured before we wrap it. Internal writes go
     through this so our own requests never re-trigger reconcile. */
  var raw = window.fetch.bind(window);

  var busy = false;
  var again = false;
  var timer = null;

  /* Gifts currently present in the cart, tracked across reconciles
     so we only celebrate a gift the moment it newly appears. */
  var present = {};

  /* ---------- transport ---------- */
  function post(url, body) {
    return raw(url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function getCart() {
    return raw(CART + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  /* ---------- cart mutations ---------- */
  function addGift(gift) {
    var props = {};
    props[PROP] = gift.key;
    return post(ADD, { items: [{ id: gift.variantId, quantity: 1, properties: props }] })
      .then(function (res) {
        // res.status is only present on an error payload (e.g. sold out).
        return !(res && res.status);
      });
  }

  /* Collapse duplicates / stray quantities back to a single unit. */
  function trim(keys) {
    var chain = Promise.resolve();
    keys.slice(1).forEach(function (k) {
      chain = chain.then(function () { return post(CHANGE, { id: k, quantity: 0 }); });
    });
    return chain
      .then(function () { return post(CHANGE, { id: keys[0], quantity: 1 }); })
      .then(function () { return true; });
  }

  function drop(keys) {
    var chain = Promise.resolve();
    keys.forEach(function (k) {
      chain = chain.then(function () { return post(CHANGE, { id: k, quantity: 0 }); });
    });
    return chain.then(function () { return true; });
  }

  /* ---------- the decision ---------- */
  function reconcile() {
    if (busy) { again = true; return; }
    busy = true;

    return getCart()
      .then(function (cart) {
        var giftKeys = {};
        GIFTS.forEach(function (g) { giftKeys[g.key] = true; });

        /* A line counts as "gift" only if WE tagged it that way, never by
           matching product/variant. That keeps a customer's genuine paid
           purchase of the same product from being mistaken for the gift
           (mislabeled in the cart, and silently excluded from the paid
           total a threshold gift depends on). */
        var byGiftKey = {};
        var paidQty = 0;
        var paidSubtotal = 0;

        (cart.items || []).forEach(function (i) {
          var tag = i.properties && i.properties[PROP];
          if (tag && giftKeys[tag]) {
            var slot = byGiftKey[tag];
            if (!slot) { slot = byGiftKey[tag] = { qty: 0, keys: [] }; }
            slot.qty += i.quantity;
            slot.keys.push(i.key);
            return;
          }
          paidQty += i.quantity;
          paidSubtotal += i.line_price;
        });

        var unlocked = [];
        var chain = Promise.resolve();
        var changed = false;

        GIFTS.forEach(function (gift) {
          var slot = byGiftKey[gift.key] || { qty: 0, keys: [] };
          var eligible = gift.mode === 'threshold'
            ? paidSubtotal >= (gift.minAmount || 0)
            : paidQty > 0;

          if (eligible && slot.qty === 0) {
            chain = chain.then(function () { return addGift(gift); })
              .then(function (ok) {
                if (ok) { changed = true; unlocked.push(gift); present[gift.key] = true; }
              });
          } else if (eligible && (slot.qty > 1 || slot.keys.length > 1)) {
            chain = chain.then(function () { return trim(slot.keys); })
              .then(function () { changed = true; });
          } else if (!eligible && slot.qty > 0) {
            chain = chain.then(function () { return drop(slot.keys); })
              .then(function () { changed = true; present[gift.key] = false; });
          } else if (eligible) {
            present[gift.key] = true;
          }
        });

        return chain.then(function () { return { changed: changed, unlocked: unlocked }; });
      })
      .then(function (result) {
        if (result.changed) sync();
        if (result.unlocked.length) announce(result.unlocked);
      })
      .catch(function () { /* never break the cart over a gift */ })
      .then(release, release);
  }

  function release() {
    busy = false;
    if (again) { again = false; schedule(); }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(reconcile, 120);
  }

  /* ---------- UI sync after we change the cart ---------- */
  function sync() {
    document.dispatchEvent(new CustomEvent('s2:cart:refresh'));
    if (document.getElementById('main-cart-items')) rerenderCartPage();
  }

  /* The cart page is server-rendered, so re-pull it and swap the
     regions Dawn owns.

     className must be copied as well as innerHTML: the empty state is
     driven by an `is-empty` class that sits on the wrappers themselves
     (#main-cart-footer and <cart-items>), not on anything inside them.
     Swapping only innerHTML leaves a populated-looking footer — totals,
     discount field and checkout button — above an empty cart. */
  function rerenderCartPage() {
    return raw(CART, { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        ['main-cart-items', 'main-cart-footer'].forEach(function (id) {
          var src = doc.getElementById(id);
          var dst = document.getElementById(id);
          if (!src || !dst) return;
          dst.innerHTML = src.innerHTML;
          dst.className = src.className;
        });

        var srcRoot = doc.querySelector('cart-items');
        var dstRoot = document.querySelector('cart-items');
        if (srcRoot && dstRoot) dstRoot.className = srcRoot.className;
      })
      .catch(function () {});
  }

  /* ---------- unlock celebration ---------- */
  function announce(gifts) {
    if (typeof window.s2GiftModal === 'function') window.s2GiftModal(gifts);
  }

  /* ---------- watch every cart mutation ---------- */
  var MUTATE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var p = raw.apply(null, arguments);
    if (MUTATE.test(url)) {
      p.then(function (res) { if (res && res.ok) schedule(); }, function () {});
    }
    return p;
  };

  /* Covers non-AJAX adds, carts built before a toggle was on,
     and landing straight on /cart. Skipped when the cart is empty
     server-side, so the common case costs nothing. */
  if (cfg.cartCount > 0) {
    if (document.readyState !== 'loading') schedule();
    else document.addEventListener('DOMContentLoaded', schedule);
  }
})();
